/**
 * GA4 Enhanced Ecommerce Tracking for mdlondon
 *
 * Wires Horizon theme events to GA4 enhanced ecommerce events.
 * Loaded as a deferred module — zero impact on LCP.
 *
 * Requires: GA4 installed via Shopify native integration (content_for_header).
 * No GTM dependency.
 *
 * Events tracked:
 *   - view_item (product page load)
 *   - view_item_list (collection page load)
 *   - select_item (product card click)
 *   - add_to_cart (product form submit / quick add)
 *   - remove_from_cart (cart quantity decrease)
 *   - view_cart (cart page load)
 *   - begin_checkout (checkout button click)
 *   - search (predictive search submit)
 *   - select_content (filter interaction)
 */

const GA4 = (() => {
  /** Safe gtag wrapper — no-ops if GA4 isn't loaded */
  function gtag() {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(arguments);
  }

  /**
   * Build a GA4 item object from product data embedded in the page.
   * @param {Object} data - Product data from Liquid
   * @returns {Object} GA4 item
   */
  function buildItem(data) {
    const item = {
      item_id: data.id,
      item_name: data.title,
      price: parseFloat(data.price) || 0,
      currency: window.__mdl_ga4?.currency || 'GBP',
    };
    if (data.variant_title && data.variant_title !== 'Default Title') {
      item.item_variant = data.variant_title;
    }
    if (data.vendor) item.item_brand = data.vendor;
    if (data.type) item.item_category = data.type;
    if (data.sku) item.item_sku = data.sku;
    if (data.quantity) item.quantity = data.quantity;
    return item;
  }

  /**
   * Build items array from cart data.
   * @param {Array} items - Cart line items
   * @returns {Array} GA4 items
   */
  function buildCartItems(items) {
    return items.map((item, index) => ({
      item_id: item.product_id || item.id,
      item_name: item.product_title || item.title,
      item_variant: item.variant_title !== 'Default Title' ? item.variant_title : undefined,
      price: (item.final_line_price || item.price) / 100,
      currency: window.__mdl_ga4?.currency || 'GBP',
      quantity: item.quantity,
      index,
    }));
  }

  // ── view_item ──────────────────────────────────────────────
  function trackViewItem() {
    const data = window.__mdl_ga4?.product;
    if (!data) return;

    gtag('event', 'view_item', {
      currency: window.__mdl_ga4.currency || 'GBP',
      value: parseFloat(data.price) || 0,
      items: [buildItem(data)],
    });
  }

  // ── view_item_list ─────────────────────────────────────────
  function trackViewItemList() {
    const data = window.__mdl_ga4?.collection;
    if (!data?.items?.length) return;

    gtag('event', 'view_item_list', {
      item_list_id: data.handle,
      item_list_name: data.title,
      items: data.items.map((item, index) => ({
        ...buildItem(item),
        index,
        item_list_id: data.handle,
        item_list_name: data.title,
      })),
    });
  }

  // ── select_item (product card click) ───────────────────────
  function trackSelectItem() {
    document.addEventListener('click', (e) => {
      const card = e.target.closest('[data-ga4-item-id]');
      if (!card) return;

      gtag('event', 'select_item', {
        items: [{
          item_id: card.dataset.ga4ItemId,
          item_name: card.dataset.ga4ItemName,
          price: parseFloat(card.dataset.ga4ItemPrice) || 0,
          currency: window.__mdl_ga4?.currency || 'GBP',
          index: parseInt(card.dataset.ga4Index, 10) || 0,
          item_list_id: card.dataset.ga4ListId || '',
          item_list_name: card.dataset.ga4ListName || '',
        }],
      });
    });
  }

  // ── add_to_cart ────────────────────────────────────────────
  function trackAddToCart() {
    document.addEventListener('cart:update', (e) => {
      const detail = e.detail || {};
      if (detail.data?.didError) return;
      if (detail.data?.source !== 'product-form-component') return;

      const productId = detail.data?.productId;
      const quantity = detail.data?.itemCount || 1;

      // Try to get product data from the page
      const data = window.__mdl_ga4?.product;
      if (data && String(data.id) === String(productId)) {
        gtag('event', 'add_to_cart', {
          currency: window.__mdl_ga4.currency || 'GBP',
          value: (parseFloat(data.price) || 0) * quantity,
          items: [{ ...buildItem(data), quantity }],
        });
        return;
      }

      // Fallback: minimal event with product ID
      gtag('event', 'add_to_cart', {
        currency: window.__mdl_ga4?.currency || 'GBP',
        items: [{ item_id: productId, quantity }],
      });
    });
  }

  // ── view_cart ──────────────────────────────────────────────
  function trackViewCart() {
    const data = window.__mdl_ga4?.cart;
    if (!data) return;

    gtag('event', 'view_cart', {
      currency: window.__mdl_ga4?.currency || 'GBP',
      value: (data.total_price || 0) / 100,
      items: buildCartItems(data.items || []),
    });
  }

  // ── begin_checkout ─────────────────────────────────────────
  function trackBeginCheckout() {
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('[name="checkout"], [href$="/checkout"], a[href*="/checkout"]');
      if (!btn) return;

      fetch('/cart.js')
        .then((r) => r.json())
        .then((cart) => {
          gtag('event', 'begin_checkout', {
            currency: window.__mdl_ga4?.currency || 'GBP',
            value: (cart.total_price || 0) / 100,
            items: buildCartItems(cart.items || []),
          });
        })
        .catch(() => {});
    });
  }

  // ── search ─────────────────────────────────────────────────
  function trackSearch() {
    // Track form submissions from search
    document.addEventListener('submit', (e) => {
      const form = e.target.closest('form[action*="/search"]');
      if (!form) return;

      const input = form.querySelector('input[name="q"]');
      if (!input?.value) return;

      gtag('event', 'search', { search_term: input.value });
    });
  }

  // ── filter interaction ─────────────────────────────────────
  function trackFilterInteraction() {
    document.addEventListener('filter:update', (e) => {
      const params = e.detail?.queryParams;
      if (!params) return;

      const filters = [...params.entries()]
        .filter(([key]) => key.startsWith('filter.'))
        .map(([key, value]) => `${key.replace('filter.', '')}=${value}`)
        .join(', ');

      if (filters) {
        gtag('event', 'select_content', {
          content_type: 'filter',
          content_id: filters,
        });
      }
    });
  }

  // ── Initialise ─────────────────────────────────────────────
  function init() {
    const template = window.__mdl_ga4?.template || '';

    // Page-specific events
    if (template === 'product') trackViewItem();
    if (template === 'collection' || template === 'search') trackViewItemList();
    if (template === 'cart') trackViewCart();

    // Global event listeners (delegated, cheap)
    trackSelectItem();
    trackAddToCart();
    trackBeginCheckout();
    trackSearch();
    trackFilterInteraction();
  }

  return { init };
})();

// Fire after DOM is ready (script is loaded deferred)
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', GA4.init);
} else {
  GA4.init();
}
